import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { appendCalculatorInput, calculateExpression, formatCalculatorNumber } from "./calculatorEngine";
import "./calculator.css";

const KEYS = [
  ["AC", "clear"], ["±", "utility"], ["%", "utility"], ["⌫", "utility"],
  ["7", "digit"], ["8", "digit"], ["9", "digit"], ["÷", "operator"],
  ["4", "digit"], ["5", "digit"], ["6", "digit"], ["×", "operator"],
  ["1", "digit"], ["2", "digit"], ["3", "digit"], ["−", "operator"],
  ["0", "digit"], [".", "digit"], ["=", "equals"], ["+", "operator"],
];
const LABELS = { "AC": "Clear all", "±": "Change sign", "%": "Percentage", "⌫": "Backspace", "÷": "Divide", "×": "Multiply", "−": "Subtract", "+": "Add", "=": "Equals", ".": "Decimal point" };
const EDGE = 12;

export default function Calculator({ onClose, anchorRef, floating = false }) {
  const [expression, setExpression] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [justEvaluated, setJustEvaluated] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [position, setPosition] = useState(null);
  const panelRef = useRef(null);
  const focusRef = useRef(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const positionRef = useRef(null);
  const priorFocus = useRef(null);

  const setSafePosition = useCallback((left, top) => {
    const rect = panelRef.current?.getBoundingClientRect();
    const width = rect?.width || 350;
    const height = rect?.height || 490;
    const next = {
      left: Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE)),
      top: Math.max(EDGE, Math.min(top, window.innerHeight - height - EDGE)),
    };
    positionRef.current = next;
    setPosition(previous => previous?.left === next.left && previous?.top === next.top ? previous : next);
  }, []);

  useEffect(() => {
    priorFocus.current = document.activeElement;
    focusRef.current?.focus({ preventScroll: true });
    return () => {
      const target = anchorRef?.current || priorFocus.current;
      if (target && target.isConnected && typeof target.focus === "function") target.focus({ preventScroll: true });
    };
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!floating) return;
    const place = () => {
      if (!panelRef.current || window.matchMedia("(max-width: 640px)").matches) return;
      if (movedRef.current && positionRef.current) {
        setSafePosition(positionRef.current.left, positionRef.current.top);
        return;
      }
      const anchor = anchorRef?.current?.getBoundingClientRect();
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      setSafePosition(anchor ? anchor.left - width - 16 : window.innerWidth - width - EDGE,
        anchor ? anchor.top + anchor.height / 2 - height / 2 : window.innerHeight - height - EDGE);
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
    };
  }, [floating, anchorRef, setSafePosition]);

  useEffect(() => {
    if (!floating) return;
    const dismiss = event => {
      if (!panelRef.current?.contains(event.target) && !anchorRef?.current?.contains(event.target)) onClose?.();
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [floating, anchorRef, onClose]);

  const press = useCallback((key) => {
    setCopied(false);
    if (key === "=") {
      try {
        const answer = calculateExpression(expression);
        setHistory(previous => [{ expression, answer }, ...previous].slice(0, 10));
        setResult(answer);
        setExpression(String(answer));
        setJustEvaluated(true);
        setError("");
      } catch (failure) {
        setError(failure.message || "Check the calculation.");
      }
      return;
    }
    if (key === "AC") { setExpression(""); setResult(null); setError(""); setJustEvaluated(false); return; }
    setExpression(previous => appendCalculatorInput(previous, key === "⌫" ? "BACK" : key === "±" ? "SIGN" : key, justEvaluated));
    setResult(null);
    setJustEvaluated(false);
    setError("");
  }, [expression, justEvaluated]);

  const handleKeyDown = (event) => {
    // Only capture keyboard input while the calculator itself has focus.
    // Prevent calculator keys from reaching the terminal's barcode hotkeys.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === "Tab") return;
    if (event.key === "Escape") { event.stopPropagation(); onClose?.(); return; }
    // Keep unrelated POS keyboard shortcuts from receiving calculator input.
    event.stopPropagation();
    const key = event.key === "Enter" || event.key === "=" ? "="
      : event.key === "Backspace" ? "⌫"
      : event.key === "Delete" ? "AC"
      : event.key === "*" ? "×" : event.key === "/" ? "÷"
      : event.key === "-" ? "−" : event.key;
    if (KEYS.some(([value]) => value === key)) {
      event.preventDefault();
      press(key);
    }
  };

  useEffect(() => {
    if (floating && historyOpen && positionRef.current && !window.matchMedia("(max-width: 640px)").matches) {
      const { left, top } = positionRef.current;
      setSafePosition(left, top);
    }
  }, [floating, historyOpen, setSafePosition]);

  const preview = (() => {
    if (!expression || justEvaluated || /[+−×÷.]$/.test(expression)) return null;
    try { return calculateExpression(expression); } catch { return null; }
  })();

  const startDrag = event => {
    if (!floating || event.button !== 0 || window.matchMedia("(max-width: 640px)").matches || event.target.closest("button")) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const drag = event => {
    if (!dragRef.current) return;
    movedRef.current = true;
    setSafePosition(dragRef.current.left + event.clientX - dragRef.current.x, dragRef.current.top + event.clientY - dragRef.current.y);
  };

  const panel = (
    <section ref={panelRef} className={`pos-calculator ${floating ? "pos-calculator--floating" : "pos-calculator--modal"}`} style={floating && position ? { left: position.left, top: position.top } : undefined}
      role="dialog" aria-modal={!floating} aria-label="Inventory Terminal Calculator"
      onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}
      onKeyDown={handleKeyDown}>
      <header className="pos-calculator__header" onPointerDown={startDrag} onPointerMove={drag} onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }}>
        <div className="pos-calculator__identity"><span aria-hidden="true" className="pos-calculator__brand">▦</span><div><strong>Calculator</strong><small>Universe Inventory Terminal</small></div></div>
        <button type="button" className="pos-calculator__close" aria-label="Close calculator" title="Close calculator" onClick={() => onClose?.()}>×</button>
      </header>
      <div className="pos-calculator__display">
        <div className="pos-calculator__expression" title={expression || ""}>{justEvaluated ? "Result" : expression || "Ready to calculate"}</div>
        <output className="pos-calculator__amount" aria-live="polite">{error ? "Error" : formatCalculatorNumber(result !== null ? result : preview !== null ? preview : 0)}</output>
        <div className="pos-calculator__display-foot">
          <span className="pos-calculator__feedback" role={error ? "alert" : undefined}>{error || (preview !== null && !justEvaluated ? "Live result" : "Keyboard & touch enabled")}</span>
          <button type="button" className="pos-calculator__copy" disabled={result === null && preview === null} onClick={async () => {
            const value = result !== null ? result : preview;
            try { await navigator.clipboard.writeText(String(value)); setCopied(true); } catch { setError("Copy is unavailable in this browser."); }
          }}>{copied ? "Copied" : "Copy"}</button>
        </div>
      </div>
      <input ref={focusRef} className="pos-calculator__keyfocus" aria-label="Calculator keyboard input" readOnly value="" />
      <div className="pos-calculator__keys" aria-label="Calculator keypad">
        {KEYS.map(([label, type]) => <button type="button" key={label} aria-label={LABELS[label] || label} className={`pos-calculator__key pos-calculator__key--${type}`} onClick={() => press(label)}>{label}</button>)}
      </div>
      <div className="pos-calculator__history">
        <button type="button" className="pos-calculator__history-toggle" aria-expanded={historyOpen} onClick={() => setHistoryOpen(value => !value)}><span>↺ Recent calculations</span><span>{historyOpen ? "⌃" : "⌄"}</span></button>
        {historyOpen && <div className="pos-calculator__history-list">{history.length ? history.map((item, index) => <button key={index} type="button" onClick={() => { setExpression(String(item.answer)); setResult(item.answer); setError(""); setJustEvaluated(true); }}>{item.expression} <strong>= {formatCalculatorNumber(item.answer)}</strong></button>) : <span>No calculations yet</span>}</div>}
      </div>
    </section>
  );
  return floating && typeof document !== "undefined" ? createPortal(panel, document.body) : panel;
}
